import type { IngestionQueueFileMeta } from "@/components/upload/IngestionQueuePanel";
import { downloadUploadedFile } from "@/services/api";

const batchFiles = new Map<string, File>();

export function registerBatchFile(fileId: string, file: File): void {
  batchFiles.set(fileId, file);
}

export function registerBatchFiles(entries: { fileId: string; file: File }[]): void {
  for (const entry of entries) {
    registerBatchFile(entry.fileId, entry.file);
  }
}

export function getBatchFile(fileId: string): File | undefined {
  return batchFiles.get(fileId);
}

export function unregisterBatchFile(fileId: string): void {
  batchFiles.delete(fileId);
}

export function clearBatchFileCache(): void {
  batchFiles.clear();
}

/**
 * Resolve the original File for a batch queue item.
 * Order: in-memory upload array → module cache → backend download.
 */
export async function resolveBatchSourceFile(
  fileId: string,
  files: File[],
  file: File | null,
  batchFileMeta: IngestionQueueFileMeta[]
): Promise<File | null> {
  const metaIndex = batchFileMeta.findIndex((entry) => entry.fileId === fileId);
  const fromUploadArray = metaIndex >= 0 ? files[metaIndex] : undefined;
  if (fromUploadArray) {
    registerBatchFile(fileId, fromUploadArray);
    return fromUploadArray;
  }

  const cached = getBatchFile(fileId);
  if (cached) {
    return cached;
  }

  if (!file && metaIndex < 0) {
    return null;
  }

  const fallbackSingle = metaIndex < 0 ? file : null;
  if (fallbackSingle) {
    registerBatchFile(fileId, fallbackSingle);
    return fallbackSingle;
  }

  const meta = batchFileMeta.find((entry) => entry.fileId === fileId);
  const fileName = meta?.fileName ?? `${fileId}.xlsx`;

  try {
    const downloaded = await downloadUploadedFile(fileId, fileName);
    registerBatchFile(fileId, downloaded);
    return downloaded;
  } catch {
    return null;
  }
}
